import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { CATEGORIES } from '../lib/constants';

const { FiSearch, FiEdit2, FiTrash2, FiPlus, FiDownload, FiEye, FiImage, FiFileText, FiChevronDown } = FiIcons;

const InventoryTable = ({ data, categoryKey, onDelete }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'inventory_id', direction: 'asc' });
  const [isExportOpen, setIsExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredData = useMemo(() => {
    let items = [...data];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(item => 
        item.inventory_id?.toLowerCase().includes(lower) || 
        item.name?.toLowerCase().includes(lower) || 
        item.article_number?.toLowerCase().includes(lower) || 
        item.manufacturer?.toLowerCase().includes(lower)
      );
    }
    if (sortConfig.key) {
      items.sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return items;
  }, [data, searchTerm, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const formatCurrency = (val) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const handleExportExcel = (onlyFiltered = false) => {
    const exportData = onlyFiltered ? filteredData : data;
    const formatted = exportData.map(item => ({
      ID: item.inventory_id,
      Hersteller: item.manufacturer,
      Artikelnummer: item.article_number,
      Name: item.name || (item.category === 'gleise' ? (item.specs?.track_type || 'Gleis') : '-'),
      Kategorie: CATEGORIES[item.category]?.label || item.category,
      Zustand: item.condition,
      Anzahl: item.count,
      Gesamtwert: formatCurrency(item.total_value)
    }));

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventar");
    XLSX.writeFile(wb, `MoBa_Export_${categoryKey}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    setIsExportOpen(false);
  };

  const handleExportPDF = (onlyFiltered = false) => {
    const exportData = onlyFiltered ? filteredData : data;
    const doc = new jsPDF();
    const categoryLabel = CATEGORIES[categoryKey]?.label || categoryKey;

    doc.setFontSize(18);
    doc.text(`MoBa Inventar - ${categoryLabel}`, 14, 20);
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 28);

    const tableColumn = ["ID", "Hersteller", "Art-Nr", "Bezeichnung", "Zustand", "Wert"];
    const tableRows = exportData.map(item => [
      item.inventory_id,
      item.manufacturer,
      item.article_number,
      item.name || (item.category === 'gleise' ? (item.specs?.track_type || 'Gleis') : '-'),
      item.condition || '-',
      formatCurrency(item.total_value)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 35,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    const totalValue = exportData.reduce((acc, curr) => acc + (curr.total_value || 0), 0);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Gesamtwert: ${formatCurrency(totalValue)}`, 14, finalY);

    doc.save(`MoBa_Inventar_${categoryKey}_${new Date().toISOString().slice(0, 10)}.pdf`);
    setIsExportOpen(false);
  };

  const getLabel = (field) => {
    const labels = { inventory_id: 'ID', manufacturer: 'Hersteller', article_number: 'Artikelnummer', name: 'Name/Typ', condition: 'Zustand', total_value: 'Wert' };
    return labels[field] || field.replace('_', ' ');
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col h-full overflow-hidden transition-colors">
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row justify-between gap-4 items-center bg-slate-50/50 dark:bg-slate-900/50">
        <div className="relative w-full sm:w-80">
          <input 
            type="text" 
            placeholder="Suchen..." 
            className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-yellow-500 text-sm outline-none transition-all bg-white dark:bg-slate-800 dark:text-white" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
          <div className="absolute left-3 top-2.5 text-slate-400">
            <SafeIcon icon={FiSearch} />
          </div>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <div className="relative" ref={exportRef}>
            <button 
              onClick={() => setIsExportOpen(!isExportOpen)}
              className={`flex items-center justify-center px-4 py-2 rounded-lg text-sm font-bold transition-all shadow-sm border ${isExportOpen ? 'bg-slate-800 dark:bg-yellow-500 text-white dark:text-slate-950 border-transparent' : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
            >
              <SafeIcon icon={FiDownload} className="mr-2" /> Export <SafeIcon icon={FiChevronDown} className={`ml-2 transition-transform ${isExportOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isExportOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700 text-[10px] font-black uppercase text-slate-400 dark:text-slate-300 border-b dark:border-slate-600">Excel Export</div>
                <button onClick={() => handleExportExcel(false)} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-yellow-50 dark:hover:bg-yellow-500/10 dark:text-white transition-colors">Alle Einträge ({data.length})</button>
                <button onClick={() => handleExportExcel(true)} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-yellow-50 dark:hover:bg-yellow-500/10 dark:text-white transition-colors border-b dark:border-slate-700">Gefilterte Ansicht ({filteredData.length})</button>
                
                <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700 text-[10px] font-black uppercase text-slate-400 dark:text-slate-300 border-b dark:border-slate-600">PDF Export</div>
                <button onClick={() => handleExportPDF(false)} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-500/10 dark:text-white transition-colors flex items-center gap-2">
                  <SafeIcon icon={FiFileText} className="text-red-500" /> Alle als PDF ({data.length})
                </button>
                <button onClick={() => handleExportPDF(true)} className="w-full text-left px-4 py-3 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-500/10 dark:text-white transition-colors flex items-center gap-2">
                  <SafeIcon icon={FiFileText} className="text-red-500" /> Gefiltert als PDF ({filteredData.length})
                </button>
              </div>
            )}
          </div>
          <button 
            onClick={() => navigate(`/inventory/${categoryKey}/new`)} 
            className="flex items-center justify-center px-4 py-2 bg-yellow-500 text-slate-900 rounded-lg hover:bg-yellow-600 text-sm font-black shadow-sm transition-all"
          >
            <SafeIcon icon={FiPlus} className="mr-2" /> NEU
          </button>
        </div>
      </div>

      <div className="overflow-x-auto flex-1">
        <table className="w-full text-left text-sm text-slate-600 dark:text-slate-400">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-400 dark:text-slate-500 uppercase font-black text-[10px] tracking-widest border-b border-slate-100 dark:border-slate-800 sticky top-0 z-10">
            <tr>
              <th className="px-6 py-4 w-16">Bild</th>
              {['inventory_id', 'manufacturer', 'article_number', 'name', 'condition', 'total_value'].map(field => (
                <th 
                  key={field} 
                  className="px-6 py-4 cursor-pointer hover:text-slate-900 dark:hover:text-white transition-colors"
                  onClick={() => handleSort(field)}
                >
                  <div className="flex items-center">
                    {getLabel(field)}
                    {sortConfig.key === field && (
                      <span className="ml-1 text-yellow-500">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-6 py-4 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
            {filteredData.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                <td className="px-6 py-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden flex items-center justify-center shadow-inner">
                    {item.images && item.images.length > 0 ? (
                      <img src={item.images[0]} className="w-full h-full object-cover" alt="Thumbnail" />
                    ) : (
                      <SafeIcon icon={FiImage} className="text-slate-300 dark:text-slate-600" />
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">{item.inventory_id}</td>
                <td className="px-6 py-4">{item.manufacturer}</td>
                <td className="px-6 py-4 font-mono text-xs">{item.article_number}</td>
                <td className="px-6 py-4 font-medium italic">
                  {item.name || (item.category === 'gleise' ? (item.specs?.track_type || item.article_number) : '-')}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${item.condition === 'Neu' ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-500' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                    {item.condition || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 font-black text-slate-900 dark:text-white">{formatCurrency(item.total_value)}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => navigate(`/inventory/${categoryKey}/${item.id}/details`)} className="p-2 text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm rounded-lg" title="Details">
                      <SafeIcon icon={FiEye} />
                    </button>
                    <button onClick={() => navigate(`/inventory/${categoryKey}/${item.id}`)} className="p-2 text-blue-600 dark:text-blue-400 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm rounded-lg" title="Bearbeiten">
                      <SafeIcon icon={FiEdit2} />
                    </button>
                    <button onClick={() => onDelete(item.id)} className="p-2 text-red-600 dark:text-red-400 hover:bg-white dark:hover:bg-slate-700 hover:shadow-sm rounded-lg" title="Löschen">
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryTable;